import { Components, Theme } from '@mui/material/styles'

/**
 * The house tooltip, matched to `packages/ui/theme/theme.ts`'s `MuiTooltip`.
 *
 * WHAT THIS USED TO BE, and why each part was wrong:
 *
 *   width: '194px'     A FIXED width on every tooltip in the app, whatever the
 *                      content. "Reset the view" -- three words -- rendered as a
 *                      194px slab with 16px/24px padding. This is the single
 *                      biggest reason they read as broken; a tooltip should be the
 *                      size of its sentence.
 *   background:        `primary.main`, which in this app is the BRAND colour. So a
 *     primary.main     tooltip's background changed per company, and on a brand
 *                      whose colour is black (DAC) it was a black slab over the
 *                      product photograph.
 *   fontSize: 12       The smallest type on screen, read at the moment of
 *                      confusion. `packages/ui` raised its own to 15 in VRG-545
 *                      for exactly this reason and noted that per-site fixes are
 *                      how it drifted to 12 in the first place.
 *   borderRadius: 4    Disagreed with the 6px used everywhere else.
 *
 * The values below are COPIED from the `packages/ui` override rather than
 * reinvented, including the dark-mode branch: a white box with dark text in light
 * mode, `#141417` with light text in dark. MUI's own greys cannot be used because
 * this theme inverts the grey ramp in dark mode, which resolves to a light box
 * with white text -- unreadable.
 *
 * ⚠️ THE DURABLE HOME IS `packages/ui`, NOT HERE. `apps/app` mounts its own theme
 * and therefore does not inherit that override, which is the whole reason this
 * file exists and the reason the two drifted. Two copies of one treatment is the
 * drift working-conventions §36 and §47 warn about, so if you change one, change
 * both -- or better, fold `apps/app`'s theme onto the shared one and delete this.
 *
 * Blast radius when this landed: THREE `<Tooltip>` call sites in `apps/app`
 * (`components/UI/Tooltip.tsx`, `PreferenceRow`, `ActionMenu`), plus the model
 * viewer's two controls. Counted, not estimated.
 */
export const MuiTooltip: Components<Theme>['MuiTooltip'] = {
  defaultProps: {
    arrow: false,
    enterDelay: 200,
  },
  styleOverrides: {
    tooltip: ({ theme }) => {
      const dark = theme.palette.mode === 'dark'
      return {
        /* No `width`. The box is sized by its text, with a ceiling so a long
           sentence wraps instead of running the width of the screen. */
        maxWidth: 280,
        backgroundColor: dark ? '#141417' : '#ffffff',
        color: dark ? '#fafafa' : theme.palette.text.primary,
        fontSize: 15,
        fontWeight: 500,
        lineHeight: 1.5,
        padding: '10px 12px',
        borderRadius: 6,
        border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
        boxShadow: theme.shadows[3],
      }
    },
    arrow: ({ theme }) => ({
      color: theme.palette.mode === 'dark' ? '#141417' : '#ffffff',
    }),
    popper: {
      '&[data-popper-placement*="top"]': {
        '.MuiTooltip-tooltip.MuiTooltip-tooltipPlacementTop ': {
          margin: '0 0 8px',
        },
      },
    },
  },
}
